import { useCallback, useRef, useState } from 'react';

export const PHASE_ORDER = ['detect', 'fetch', 'pipeline', 'verify', 'persist'];
const TERMINAL = new Set(['done', 'skipped', 'error']);
const stripEmoji = (s) => (s || '').replace(/^[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}️\s]+/u, '').trim();

const initial = () => ({ status: 'idle', phases: [], progress: { current: 0, total: PHASE_ORDER.length, label: '' }, result: null, rawLog: [] });

export function useScrapeStream() {
  const [state, setState] = useState(initial);
  const esRef = useRef(null);
  const onCloseRef = useRef(null);
  const stateRef = useRef(state); stateRef.current = state;  // for onClose(finalState)

  const stop = useCallback(() => {
    if (esRef.current) { try { esRef.current.close(); } catch (_e) {} esRef.current = null; }
  }, []);

  const start = useCallback(({ url, eventSourceFactory, onClose }) => {
    stop();
    onCloseRef.current = onClose || null;
    setState({ ...initial(), status: 'running' });
    const es = eventSourceFactory ? eventSourceFactory(url) : new EventSource(url);
    esRef.current = es;
    const parse = (e) => { try { return JSON.parse(e.data); } catch (_x) { return null; } };

    es.addEventListener('phase', (e) => {
      const d = parse(e); if (!d) return;
      setState(prev => {
        const phases = prev.phases.some(p => p.key === d.key)
          ? prev.phases.map(p => p.key === d.key ? { ...p, label: d.label, status: d.status, detail: d.detail } : p)
          : [...prev.phases, { key: d.key, label: d.label, status: d.status, detail: d.detail, substeps: [] }];
        const current = phases.filter(p => TERMINAL.has(p.status)).length;
        const running = phases.find(p => p.status === 'running');
        return { ...prev, phases, progress: { current, total: PHASE_ORDER.length, label: running ? running.label : prev.progress.label } };
      });
    });
    es.addEventListener('step', (e) => {
      const d = parse(e); if (!d) return;
      setState(prev => {
        const key = 'pipeline';
        const phases = prev.phases.map(p => p.key === key ? { ...p, substeps: [...(p.substeps || []), d] } : p);
        return { ...prev, phases };
      });
    });
    es.addEventListener('result', (e) => {
      const d = parse(e); if (!d) return;
      setState(prev => ({ ...prev, result: { ...(d.data || d), technique: d.technique || (d.data || {}).technique } }));
    });
    es.addEventListener('log', (e) => {
      const d = parse(e); if (!d) return;
      const msg = stripEmoji(d.log || d.msg);
      if (msg) setState(prev => ({ ...prev, rawLog: [...prev.rawLog, msg] }));
    });
    es.addEventListener('scrape_error', (e) => {
      const d = parse(e);
      setState(prev => ({ ...prev, status: 'error', rawLog: [...prev.rawLog, (d && d.message) || 'Scrape failed'] }));
    });
    es.addEventListener('close', () => {
      setState(prev => {
        const ok = prev.status !== 'error' && !!prev.result;
        return { ...prev, status: ok ? 'success' : 'error',
                 // snap to 100% on success so a skipped phase (e.g. persist on a Test run) doesn't leave the bar short
                 progress: { ...prev.progress, current: ok ? prev.progress.total : prev.progress.current,
                             label: ok ? 'Done' : 'Failed' } };
      });
      stop();
      if (onCloseRef.current) { try { onCloseRef.current(stateRef.current); } catch (_e) {} }
    });
    es.onerror = () => {
      setState(prev => ({ ...prev, status: prev.result ? 'success' : 'error' }));
      stop();
      if (onCloseRef.current) { try { onCloseRef.current(stateRef.current); } catch (_e) {} }
    };
  }, [stop]);

  return { state, start, stop };
}
